import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV } from "@/constants/testIds";

const NAV_GROUPS = [
  {
    slug: "about",
    label: "About",
    items: [
      { to: "/", label: "Home" },
      { to: "/about", label: "About the Centre" },
    ],
  },
  {
    slug: "focus",
    label: "Focus & Ethics",
    items: [
      { to: "/focus-areas", label: "Focus Areas" },
      { to: "/ethical-ai", label: "Responsible AI and Data" },
    ],
  },
  {
    slug: "knowledge",
    label: "Knowledge",
    items: [
      { to: "/frameworks", label: "Standards and Assurance" },
      { to: "/sdgs", label: "Sustainable Development Goals" },
      { to: "/insights", label: "Open Data Sources" },
      { to: "/foresight", label: "Foresight Observatory" },
      { to: "/media", label: "Media" },
    ],
  },
  {
    slug: "connect",
    label: "Connect",
    items: [
      { to: "/partnerships", label: "Partnerships" },
      { to: "/resources", label: "Resources" },
      { to: "/executive-education", label: "Executive Education" },
      { to: "/get-involved", label: "Get Involved" },
    ],
  },
];

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB]">
      <div className="ccdr-container flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="flex items-center gap-3"
          aria-label="Centre for Climate and Disaster Resilience — home"
        >
          <img
            src="/ccdr-logo.png"
            alt="CCDR logo"
            className="w-12 h-12 object-contain"
            width="48"
            height="48"
          />
          <span className="hidden sm:flex flex-col leading-none">
            <span
              className="text-[15px] font-medium"
              style={{ color: "var(--ccdr-primary-dark)" }}
            >
              Centre for Climate and Disaster Resilience
            </span>
            <span className="text-[11px] mt-1 tracking-[0.18em] uppercase" style={{ color: "var(--ccdr-text-muted)" }}>
              Independent. Evidence-led.
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_GROUPS.map((group) => (
            <DropdownMenu key={group.slug}>
              <DropdownMenuTrigger
                data-testid={NAV.group(group.slug)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm tracking-wide outline-none transition-colors hover:text-[var(--ccdr-primary)]"
              >
                {group.label}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="rounded-none border border-[#E5E7EB] bg-white p-1 min-w-[240px]"
              >
                {group.items.map((it) => (
                  <DropdownMenuItem key={it.to} asChild>
                    <NavLink
                      to={it.to}
                      data-testid={NAV.link(slugify(it.label))}
                      className={({ isActive: a }) =>
                        `block px-4 py-2.5 text-sm cursor-pointer rounded-none transition-colors ${
                          a || isActive(it.to)
                            ? "text-[var(--ccdr-primary)] bg-[#F3F4F6]"
                            : "text-[#111827] hover:bg-[#F3F4F6] hover:text-[var(--ccdr-primary)]"
                        }`
                      }
                    >
                      {it.label}
                    </NavLink>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
          <Link
            to="/get-involved"
            data-testid={NAV.newsletterButton}
            className="ml-4 ccdr-btn-primary"
          >
            Request a briefing
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid={NAV.toggle}
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E7EB] bg-white">
          <div className="ccdr-container py-6 space-y-6">
            {NAV_GROUPS.map((group) => (
              <div key={group.slug}>
                <p className="ccdr-overline mb-3">{group.label}</p>
                <div className="space-y-2">
                  {group.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={NAV.link(slugify(it.label))}
                      onClick={() => setOpen(false)}
                      className={({ isActive: a }) =>
                        `block py-2 text-base ${
                          a || isActive(it.to) ? "text-[var(--ccdr-primary)]" : "text-[#111827]"
                        }`
                      }
                    >
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
            <Link
              to="/get-involved"
              onClick={() => setOpen(false)}
              className="ccdr-btn-primary w-full justify-center"
            >
              Request a briefing
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
